import React, { useRef } from 'react';
import {
  VStack,
  Text,
  useColorModeValue,
  Box,
  Skeleton,
  SkeletonCircle,
  HStack,
} from '@chakra-ui/react';
import { Scroll } from '@phosphor-icons/react';
import CommentItem from './CommentItem';

const LoadingSkeleton = () => (
  <Box
    p={4}
    bg={useColorModeValue('ancient.parchment', 'gray.700')}
    borderRadius="md"
    borderWidth="1px"
    borderColor={useColorModeValue('ancient.stone', 'whiteAlpha.300')}
  >
    <HStack align="start" spacing={4}>
      <SkeletonCircle size="10" />
      <VStack align="stretch" flex={1} spacing={3}>
        <HStack>
          <Skeleton height="20px" width="120px" />
          <Skeleton height="20px" width="80px" />
        </HStack>
        <Skeleton height="40px" />
      </VStack>
    </HStack>
  </Box>
);

const CommentList = ({
  comments,
  loadingComments,
  user,
  siteId,
  onLike,
  onReply,
  replyTo,
  handleReplyClick,
  collapsedThreads,
  toggleThread
}) => {
  const borderColor = useColorModeValue('ancient.stone', 'whiteAlpha.300');
  const commentRefs = useRef({});

  if (loadingComments) {
    return (
      <VStack align="stretch" spacing={4}>
        <LoadingSkeleton />
        <LoadingSkeleton />
        <LoadingSkeleton />
      </VStack>
    );
  }

  if (comments.length === 0) {
    return (
      <Box
        p={6}
        textAlign="center"
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="md"
        bg={useColorModeValue('ancient.parchment', 'gray.700')}
        position="relative"
        _before={{
          content: '""',
          position: 'absolute',
          top: '2px',
          left: '2px',
          right: '2px',
          bottom: '2px',
          borderRadius: 'md',
          border: '1px solid',
          borderColor: 'ancient.stone',
          opacity: 0.3,
          pointerEvents: 'none'
        }}
      >
        <Box 
          mb={3} 
          color="ancient.terracotta"
          display="flex"
          justifyContent="center"
        >
          <Scroll size={32} weight="duotone" />
        </Box>
        <Text 
          textStyle="hieroglyph"
          fontSize="lg"
          color={useColorModeValue('ancient.stone', 'whiteAlpha.900')}
        >
          No comments yet. Be the first to comment!
        </Text>
      </Box>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      {comments.map((comment) => (
        <CommentItem
          key={comment.id}
          comment={comment}
          user={user}
          siteId={siteId}
          onLike={onLike}
          onReply={onReply}
          replyTo={replyTo}
          handleReplyClick={handleReplyClick}
          collapsedThreads={collapsedThreads}
          toggleThread={toggleThread}
          commentRef={el => commentRefs.current[comment.id] = el}
          borderColor={borderColor}
          isTopLevel={true}
        />
      ))}
    </VStack>
  );
};

export default CommentList;
